import * as _ from 'lodash-es';
import {AsyncSubject, combineLatest, forkJoin, merge, MonoTypeOperatorFunction, Observable, of, Subject} from 'rxjs';
import {filter, switchMap, tap} from 'rxjs/operators';
import {Widget} from '../../../components/layout/widget/widget';
import {WidgetSettings} from '../../../components/layout/widget/widget-settings';
import {FilterCriterion} from '../../../models/filters/filter-criterion';
import {hasSettingUpdateField} from '../common-widget.implementation';

interface WidgetSettingsWithFilters extends WidgetSettings {
    filters: any;
}

type ConverterFromFilterToSettings = (filterQuery: any) => Observable<any>;
type ConvertersFromSettingsToFilter = (settingsValue: any) => Observable<any>;

interface WidgetFilterOptions<FILTERS_TYPE,
    SETTINGS extends WidgetSettings = WidgetSettingsWithFilters,
    FILTERS_KEY extends keyof SETTINGS = any> {

    settingsFieldName: FILTERS_KEY;
    convertersFromFilterToSettings?: Partial<Record<keyof FILTERS_TYPE, ConverterFromFilterToSettings>>;
    convertersFromSettingsToFilter?: Partial<Record<keyof SETTINGS[FILTERS_KEY], ConvertersFromSettingsToFilter>>;
    filterSettingsNameMap?: Record<keyof FILTERS_TYPE, keyof SETTINGS[FILTERS_KEY]>;
}

/**
 * A workaround, that is used in {@link WidgetFilter.bindFiltersAndSettings}
 * to make sure, that initial fetching will be called initially,
 * regardless if filters in settings are empty or not.
 */
export const initialFetchForBindFiltersAndSettings = <T>(): MonoTypeOperatorFunction<T> => {
    let isDefaultTriggerStopped: boolean = false;
    const defaultFetch$ = of(undefined).pipe(
        filter(() => !isDefaultTriggerStopped)
    );

    return bindings$ => merge(
        bindings$.pipe(
            tap(() => {
                if (!isDefaultTriggerStopped) {
                    isDefaultTriggerStopped = true;
                }
            })
        ),
        defaultFetch$
    );
};

export class WidgetFilter<FILTERS_TYPE,
    SETTINGS extends WidgetSettings = WidgetSettingsWithFilters,
    FILTERS_KEY extends keyof SETTINGS = any> {

    private readonly updateFilterInputs$ = new Subject<void>();

    private options: WidgetFilterOptions<FILTERS_TYPE, SETTINGS, FILTERS_KEY> = {
        settingsFieldName: 'filters' as FILTERS_KEY
    };

    constructor(
        private widget: Widget,
        public filters: Record<keyof FILTERS_TYPE, FilterCriterion>,
        options?: Partial<WidgetFilterOptions<FILTERS_TYPE, SETTINGS>>
    ) {
        if (options) {
            Object.assign(this.options, options);
        }
    }

    public onChange(): void {
        this.updateFilterInputs$.next();
    }

    public getFilterCriterion(): FilterCriterion[] {
        return Object.values(this.filters);
    }

    public bindFiltersAndSettings(): Observable<any> {
        const updateFilters = (newFilters: FILTERS_TYPE) =>
            _.forEach(
                newFilters as Record<keyof FILTERS_TYPE, any>,
                (filterValue, filterName) =>
                    this.filters[filterName as keyof FILTERS_TYPE].query = filterValue
            );

        const updateSettings = (newSettings: SETTINGS[FILTERS_KEY]) =>
            this.widget.implementationInstance.updateSettings({
                [this.options.settingsFieldName]: {
                    ...this.getSettings(),
                    ...newSettings
                }
            } as Partial<WidgetSettingsWithFilters>);

        const isFiltersInitialized$ = new AsyncSubject<boolean>();
        const markFiltersAsInitialized = () => {
            isFiltersInitialized$.next(true);
            isFiltersInitialized$.complete();
        };

        const settingsWithUpdates$ = (this.widget.settingsWithUpdates$ as Observable<Partial<SETTINGS>>).pipe(
            filter(settingsUpdates => hasSettingUpdateField(settingsUpdates, this.options.settingsFieldName)),
            switchMap(() => this.getFilterValuesBySettings()),
            filter(newFilters => {
                const hasChanges = !_.isEqual(newFilters, this.getFiltersValues());

                if (!hasChanges) {
                    markFiltersAsInitialized();
                }

                return hasChanges;
            }),
            tap(updateFilters),
            tap(markFiltersAsInitialized)
        );

        /**
         * Listen to changes in filters only after
         * the filters are updated from settings at least once.
         */
        const filterChange$ = combineLatest([
            this.updateFilterInputs$,
            isFiltersInitialized$
        ]).pipe(
            switchMap(() => this.getSettingsByFilters()),
            filter(newSettings => !_.isEqual(newSettings, this.getSettings())),
            switchMap(updateSettings)
        );

        return merge(
            settingsWithUpdates$,
            filterChange$
        ).pipe(
            initialFetchForBindFiltersAndSettings()
        );
    }

    private convertFromFilterToSettings(filterName: keyof FILTERS_TYPE): Observable<any> {
        const filterCriterion: FilterCriterion = this.filters[filterName];

        const converter: ConverterFromFilterToSettings = this.options.convertersFromFilterToSettings
            && this.options.convertersFromFilterToSettings[filterName];

        return converter
            ? converter(filterCriterion.query)
            : of(filterCriterion.query);
    }

    private convertFromSettingsToFilter(settingName: keyof SETTINGS[FILTERS_KEY]): Observable<any> {
        const settings = this.getSettings();
        const settingsValue = settings[settingName];

        const converter: ConverterFromFilterToSettings = this.options.convertersFromSettingsToFilter
            && this.options.convertersFromSettingsToFilter[settingName];

        return converter
            ? converter(settingsValue)
            : of(settingsValue);
    }

    private getSettings(): SETTINGS[FILTERS_KEY] {
        return (this.widget.implementationInstance.settings as SETTINGS)[this.options.settingsFieldName]
            || {} as SETTINGS[FILTERS_KEY];
    }

    private getFiltersValues(): FILTERS_TYPE {
        return _.mapValues(
            this.filters,
            (filterCriterion: FilterCriterion) => filterCriterion.query
        );
    }

    private getSettingNameByFilterName(filterName: keyof FILTERS_TYPE): keyof SETTINGS[FILTERS_KEY] {
        const mapping = this.options.filterSettingsNameMap;

        return (
            mapping
            && mapping[filterName]
            || filterName
        ) as keyof SETTINGS[FILTERS_KEY];
    }

    private getSettingsByFilters(): Observable<SETTINGS[FILTERS_KEY]> {
        return forkJoin(
            _.transform(
                this.filters,
                (acc, filterCriterion, filterName) => {
                    const settingName = this.getSettingNameByFilterName(filterName as keyof FILTERS_TYPE);

                    acc[settingName] = this.convertFromFilterToSettings(filterName as keyof FILTERS_TYPE);
                },
                {} as Record<keyof SETTINGS[FILTERS_KEY], Observable<SETTINGS[FILTERS_KEY]>>
            )
        ) as any;
    }

    private getFilterValuesBySettings(): Observable<FILTERS_TYPE> {
        return forkJoin(
            _.transform(
                this.filters,
                (acc, filterCriterion, filterName: keyof FILTERS_TYPE) => {
                    const settingName = this.getSettingNameByFilterName(filterName as keyof FILTERS_TYPE);

                    acc[filterName] = this.convertFromSettingsToFilter(settingName as keyof SETTINGS[FILTERS_KEY]);
                },
                {} as Record<keyof FILTERS_TYPE, Observable<FILTERS_TYPE>>
            )
        ) as any;
    }

}
